// ABOUTME: Presents the configured MCP address and client connection instructions.
// ABOUTME: Keeps onboarding text in metadata and clipboard failures recoverable.
import React from "react";
import { Box, Button, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";

function resourceAddress(apiOrigin?: string): string | null {
    try {
        const origin = new URL(apiOrigin || "");
        if (
            origin.protocol !== "https:" ||
            origin.pathname !== "/" ||
            origin.username ||
            origin.password ||
            origin.search ||
            origin.hash
        )
            return null;
        return new URL("/mcp", origin).href;
    } catch {
        return null;
    }
}

export default function McpConnectionSetup({
    apiOrigin
}: {
    apiOrigin?: string;
}) {
    const [t] = useTranslation("MCP", { useSuspense: false });
    const address = resourceAddress(apiOrigin);
    const [copyStatus, setCopyStatus] = React.useState("");
    const [copying, setCopying] = React.useState(false);

    async function copyAddress() {
        if (!address || copying) return;
        setCopying(true);
        setCopyStatus("");
        try {
            await navigator.clipboard.writeText(address);
            setCopyStatus("addressCopied");
        } catch {
            setCopyStatus("copyAddressFailed");
        } finally {
            setCopying(false);
        }
    }

    return (
        <Box mt={2} mb={3} p={2} bgcolor="grey.50" borderRadius={4}>
            <Typography variant="h6" component="h2" gutterBottom>
                {t("connectionSetupTitle")}
            </Typography>
            <Typography variant="body2" paragraph>
                {t("connectionSetupDescription")}
            </Typography>
            {address ? (
                <>
                    <Box
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                    >
                        <TextField
                            id="mcp-server-address"
                            label={t("mcpAddress")}
                            value={address}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            onFocus={(event) => event.target.select()}
                        />
                        <Box
                            ml={{ xs: 0, sm: 2 }}
                            mt={{ xs: 1, sm: 0 }}
                            flexShrink={0}
                        >
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={copying}
                                onClick={copyAddress}
                            >
                                {t("copyAddress")}
                            </Button>
                        </Box>
                    </Box>
                    <Typography
                        variant="body2"
                        role="status"
                        aria-live="polite"
                    >
                        {copyStatus ? t(copyStatus) : ""}
                    </Typography>
                    <Box mt={2}>
                        {["chatgpt", "claude"].map((client) => (
                            <React.Fragment key={client}>
                                <Typography variant="subtitle1" component="h3">
                                    {t(`${client}InstructionsTitle`)}
                                </Typography>
                                <Box component="ol" mt={1} pl={3}>
                                    {[1, 2, 3, 4].map((step) => (
                                        <Typography
                                            key={step}
                                            component="li"
                                            variant="body2"
                                            gutterBottom
                                        >
                                            {t(`${client}Step${step}`)}
                                        </Typography>
                                    ))}
                                </Box>
                            </React.Fragment>
                        ))}
                        <Typography variant="body2" paragraph>
                            {t("connectionSetupAvailability")}
                        </Typography>
                        <Typography variant="body2">
                            {t("connectionSetupComplete")}
                        </Typography>
                    </Box>
                </>
            ) : (
                <Typography variant="body2" color="error" role="alert">
                    {t("addressUnavailable")}
                </Typography>
            )}
        </Box>
    );
}
